/**
 * IndexedDB Queue Manager for Background Sync
 *
 * Manages a queue of failed API mutations that will be replayed when the network
 * is restored using the Background Sync API.
 */

const DB_NAME = 'evrls-sync';
const STORE_NAME = 'pending-requests';
const DB_VERSION = 1;
const MAX_RETRIES = 3;
const MAX_AGE_DAYS = 7;

export interface QueuedRequest {
  id: string;
  url: string;
  method: string;
  body: string;
  headers: Record<string, string>;
  timestamp: number;
  retryCount: number;
  businessId?: string;
  metadata?: {
    type?: 'booking' | 'payment' | 'customer' | 'other';
    optimisticId?: string;
  };
}

/**
 * Initialize IndexedDB database
 */
export async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id' });

        // Create indexes for efficient querying
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('businessId', 'businessId', { unique: false });
        objectStore.createIndex('retryCount', 'retryCount', { unique: false });
      }
    };
  });
}

/**
 * Queue a failed request for later sync
 */
export async function queueRequest(request: Omit<QueuedRequest, 'id' | 'timestamp' | 'retryCount'>): Promise<string> {
  const db = await initDB();
  const id = crypto.randomUUID();

  const queuedRequest: QueuedRequest = {
    id,
    ...request,
    timestamp: Date.now(),
    retryCount: 0,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const addRequest = store.add(queuedRequest);

    addRequest.onsuccess = () => resolve(id);
    addRequest.onerror = () => reject(addRequest.error);
  });
}

/**
 * Get all pending requests from the queue
 */
export async function getPendingRequests(): Promise<QueuedRequest[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      // Filter out requests that have exceeded max retries or are too old
      const now = Date.now();
      const maxAge = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

      const validRequests = (request.result as QueuedRequest[]).filter(
        (req) => req.retryCount < MAX_RETRIES && (now - req.timestamp) < maxAge
      );

      resolve(validRequests);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Get a single request by ID
 */
export async function getRequest(id: string): Promise<QueuedRequest | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a request from the queue (after successful sync)
 */
export async function removeRequest(id: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Increment retry count for a failed sync attempt
 */
export async function incrementRetryCount(id: string): Promise<void> {
  const db = await initDB();
  const request = await getRequest(id);

  if (!request) {
    throw new Error(`Request ${id} not found`);
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const updatedRequest: QueuedRequest = {
      ...request,
      retryCount: request.retryCount + 1,
    };

    const updateRequest = store.put(updatedRequest);

    updateRequest.onsuccess = () => resolve();
    updateRequest.onerror = () => reject(updateRequest.error);
  });
}

/**
 * Get count of pending requests
 */
export async function getPendingCount(): Promise<number> {
  const requests = await getPendingRequests();
  return requests.length;
}

/**
 * Clear all pending requests (use with caution!)
 */
export async function clearQueue(): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Prune old entries that are beyond the max age or max retries
 */
export async function pruneOldEntries(): Promise<number> {
  const db = await initDB();
  const now = Date.now();
  const maxAge = MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.openCursor();

    let prunedCount = 0;

    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;

      if (cursor) {
        const req = cursor.value as QueuedRequest;
        const age = now - req.timestamp;

        // Delete if too old or exceeded max retries
        if (age > maxAge || req.retryCount >= MAX_RETRIES) {
          cursor.delete();
          prunedCount++;
        }

        cursor.continue();
      } else {
        // No more entries
        resolve(prunedCount);
      }
    };

    request.onerror = () => reject(request.error);
  });
}

/**
 * Get requests by business ID
 */
export async function getRequestsByBusiness(businessId: string): Promise<QueuedRequest[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('businessId');
    const request = index.getAll(businessId);

    request.onsuccess = () => resolve(request.result as QueuedRequest[]);
    request.onerror = () => reject(request.error);
  });
}
